import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { MultiSectionFiltersFacetsWidget } from '~/components/common/filters/MultiSectionFiltersFacetsWidget'
import type { FilterSectionData } from '~/components/common/filters/MultiSectionFiltersFacetsWidget/FilterSections'
import { DEFAULT_CHARGE_SEARCH_FILTERS } from '~/constants/chargeSearchFilters'
import { SectionKey } from '~/constants/sectionKeys'
import useDeferredCall from '~/utils/useDeferredCall'

interface SearchChargeMultiFiltersFacetsWidgetProps {
  facets?: FilterSectionData
  filters?: FilterSectionData
  onFacetsChange?: (facets: FilterSectionData) => void
  onFiltersChange?: (filters: FilterSectionData) => void
  resultsCount?: number
}

const FiltersAndFacetsWidget: FC<SearchChargeMultiFiltersFacetsWidgetProps> = ({
  facets: facetsProp,
  filters: filtersProp,
  onFacetsChange,
  onFiltersChange,
  resultsCount,
}) => {
  const { t } = useTranslation(['Search', 'Common'])
  const [filters, setFilters] = useState<FilterSectionData>(
    filtersProp || DEFAULT_CHARGE_SEARCH_FILTERS,
  )

  const [facets, setFacets] = useState<FilterSectionData>(facetsProp || {})

  // Use refs to track previous prop values to prevent unnecessary updates
  const prevFiltersRef = useRef<FilterSectionData | undefined>()
  const prevFacetsRef = useRef<FilterSectionData | undefined>()

  // Deferred calls - cleaner than manual ref + useEffect pattern
  const triggerFiltersCall = useDeferredCall(onFiltersChange)
  const triggerFacetsCall = useDeferredCall(onFacetsChange)

  // Create section titles mapping using SectionKey enum
  const sectionTitles = useMemo(
    () => ({
      [SectionKey.PATIENT]: t('Common:PATIENT'),
      [SectionKey.APPOINTMENT]: t('Common:APPOINTMENT'),
      [SectionKey.FULFILLMENT]: t('Common:FULFILLMENT'),
      [SectionKey.CATEGORIES]: t('Common:CATEGORIES'),
    }),
    [t],
  )

  useEffect(() => {
    // Reset filters when filtersProp changes, including when it's reset to defaults
    if (filtersProp !== prevFiltersRef.current) {
      setFilters(filtersProp || DEFAULT_CHARGE_SEARCH_FILTERS)
      prevFiltersRef.current = filtersProp
    }
  }, [filtersProp])

  useEffect(() => {
    // Reset facets when facetsProp changes or when it becomes empty/undefined
    if (facetsProp !== prevFacetsRef.current) {
      setFacets(facetsProp || {})
      prevFacetsRef.current = facetsProp
    }
  }, [facetsProp])

  const handleFilterToggle = useCallback(
    (sectionKey: string, itemId: string | number, enabled: boolean) => {
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters }
        if (updatedFilters[sectionKey]) {
          updatedFilters[sectionKey] = updatedFilters[sectionKey].map((item) =>
            item.id === itemId ? { ...item, enabled } : item,
          )
        }

        // Trigger callback with the updated filters
        triggerFiltersCall(updatedFilters)
        return updatedFilters
      })
    },
    [triggerFiltersCall],
  )

  const handleFacetToggle = useCallback(
    (sectionKey: string, itemId: string | number, enabled: boolean) => {
      setFacets((prevFacets) => {
        const updatedFacets = { ...prevFacets }
        if (updatedFacets[sectionKey]) {
          updatedFacets[sectionKey] = updatedFacets[sectionKey].map((item) =>
            item.id === itemId ? { ...item, enabled } : item,
          )
        }

        // Trigger callback with the updated facets
        triggerFacetsCall(updatedFacets)
        return updatedFacets
      })
    },
    [triggerFacetsCall],
  )

  const handleDeselectSection = useCallback(
    (sectionKey: string, sectionType: 'filter' | 'facet') => {
      if (sectionType === 'filter') {
        setFilters((prevFilters) => {
          const updatedFilters = { ...prevFilters }
          if (updatedFilters[sectionKey]) {
            updatedFilters[sectionKey] = updatedFilters[sectionKey].map(
              (item) => ({ ...item, enabled: false }),
            )
          }

          // Trigger callback with the updated filters
          triggerFiltersCall(updatedFilters)
          return updatedFilters
        })
      } else if (sectionType === 'facet') {
        setFacets((prevFacets) => {
          const updatedFacets = { ...prevFacets }
          if (updatedFacets[sectionKey]) {
            updatedFacets[sectionKey] = updatedFacets[sectionKey].map(
              (item) => ({ ...item, enabled: false }),
            )
          }

          // Trigger callback with the updated facets
          triggerFacetsCall(updatedFacets)
          return updatedFacets
        })
      }
    },
    [triggerFiltersCall, triggerFacetsCall],
  )

  const handleSelectSection = useCallback(
    (sectionKey: string, sectionType: 'filter' | 'facet') => {
      if (sectionType === 'filter') {
        setFilters((prevFilters) => {
          const updatedFilters = { ...prevFilters }
          if (updatedFilters[sectionKey]) {
            updatedFilters[sectionKey] = updatedFilters[sectionKey].map(
              (item) => ({ ...item, enabled: true }),
            )
          }

          // Trigger callback with the updated filters
          triggerFiltersCall(updatedFilters)
          return updatedFilters
        })
      } else if (sectionType === 'facet') {
        setFacets((prevFacets) => {
          const updatedFacets = { ...prevFacets }
          if (updatedFacets[sectionKey]) {
            updatedFacets[sectionKey] = updatedFacets[sectionKey].map(
              (item) => ({ ...item, enabled: true }),
            )
          }

          // Trigger callback with the updated facets
          triggerFacetsCall(updatedFacets)
          return updatedFacets
        })
      }
    },
    [triggerFiltersCall, triggerFacetsCall],
  )

  return (
    <MultiSectionFiltersFacetsWidget
      enableDeselectAllButton={{
        [SectionKey.APPOINTMENT]: false,
        '*': true,
      }}
      enableSelectAllButton={{
        [SectionKey.PATIENT]: true,
      }}
      facets={facets}
      filters={filters}
      resultsCount={resultsCount}
      sectionTitles={sectionTitles}
      title={t('Search:SEARCH_RESULTS')}
      onDeselectSection={handleDeselectSection}
      onFacetToggle={handleFacetToggle}
      onFilterToggle={handleFilterToggle}
      onSelectSection={handleSelectSection}
    />
  )
}

export default FiltersAndFacetsWidget
